/**
 * Application entry point: routing, view switching and service worker setup.
 *
 * Routes are hash-based (#/today/2026-10-02), which keeps deep links working on
 * a static host with no redirect rules, and offline with no network at all.
 */

import './styles/app.css';
import './styles/print.css';

import { $, $$, toast } from './lib/dom.js';
import { addDays, today } from './lib/dates.js';
import { setSaveStatus } from './lib/autosave.js';
import { getStoragePersistence } from './lib/persist.js';
import { renderToday } from './views/today.js';
import { renderWeek } from './views/week.js';
import { renderMonth } from './views/month.js';
import { renderSettings } from './views/settings.js';

const VIEWS = {
  today: renderToday,
  week: renderWeek,
  month: renderMonth,
  settings: renderSettings,
};

const TITLES = {
  today: 'Today',
  week: 'Week',
  month: 'Month',
  settings: 'Settings',
};

/** Whatever view is on screen registers its autosave here so it can be flushed. */
let pendingSave = null;

const app = {
  /** Navigate, letting the hashchange handler do the rendering. */
  go(hash) {
    if (location.hash === hash) render();
    else location.hash = hash;
  },
  /** Re-render the current route, e.g. after a restore or a data wipe. */
  refresh() {
    render();
  },
  setPendingSave(save) {
    pendingSave = save;
  },
};

/** Parse "#/week/2026-10-02" into { view, date }. */
function parseRoute() {
  const raw = location.hash.replace(/^#\/?/, '');
  const [view, date] = raw.split('/');
  return {
    view: VIEWS[view] ? view : 'today',
    date: date || null,
  };
}

let rendering = false;

async function render() {
  if (rendering) return;
  rendering = true;

  // Commit anything the previous view had in flight before replacing it.
  if (pendingSave) {
    try {
      await pendingSave.flush();
    } catch (err) {
      console.error('Could not save before leaving the view', err);
    }
    pendingSave = null;
  }

  const { view, date } = parseRoute();
  const main = $('#main');

  for (const tab of $$('#tabs .tab')) {
    const isCurrent = tab.dataset.view === view;
    if (isCurrent) tab.setAttribute('aria-current', 'page');
    else tab.removeAttribute('aria-current');
  }

  document.title = `${TITLES[view]} — Timesheet`;

  try {
    await VIEWS[view](main, { date }, app);
  } catch (err) {
    console.error('Could not render', view, err);
    main.replaceChildren();
    const message = document.createElement('div');
    message.className = 'card';
    message.innerHTML =
      '<h2 class="card__title">Something went wrong</h2>' +
      '<p class="section-note">The view could not be loaded. Your data is untouched. Try reloading the app.</p>';
    main.append(message);
  } finally {
    rendering = false;
  }

  setSaveStatus('idle');
}

window.addEventListener('hashchange', render);

/** Save immediately if the app is backgrounded, closed or switched away from. */
function flushOnHide() {
  if (pendingSave) pendingSave.flush();
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushOnHide();
});
window.addEventListener('pagehide', flushOnHide);

/** Keyboard shortcuts for moving between days without reaching for the mouse. */
document.addEventListener('keydown', (event) => {
  // Never steal a keystroke from a field the user is typing in.
  const target = event.target;
  const typing =
    target instanceof HTMLElement &&
    (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
  if (typing || event.metaKey || event.ctrlKey || event.altKey) return;

  const { view, date } = parseRoute();
  if (view !== 'today') return;
  const current = date || today();
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    app.go(`#/today/${addDays(current, -1)}`);
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    app.go(`#/today/${addDays(current, 1)}`);
  }
});

// --- Service worker ---------------------------------------------------------

/**
 * Register the worker in production builds only; in dev it would serve stale
 * modules. The worker is emitted at the site root by the Vite plugin.
 */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    // Resolve against the page rather than import.meta.url: the bundle lives in
    // assets/, but the worker is emitted at the site root so it can claim the
    // whole app as its scope. Building the URL from location.href also keeps
    // this working when the site is served from a subdirectory.
    const swUrl = new URL('sw.js', window.location.href).href;
    navigator.serviceWorker.register(swUrl, { scope: './' }).catch((err) => {
      console.warn('Service worker registration failed; the app still works online.', err);
    });
  });

  // When a new deployment takes over, the next load picks it up. Tell the user
  // rather than reloading under them mid-edit.
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    toast('App updated. Reload to use the new version.');
  });
}

// --- Start ------------------------------------------------------------------

/*
 * Ask the browser to treat our storage as persistent rather than evictable.
 * This is fired once on first load; the answer is cached for the session and
 * the Settings screen reports it. Nothing waits on it — the reminder logic
 * awaits the same promise when it needs the answer.
 */
getStoragePersistence();

if (!location.hash) location.hash = '#/today';
render();
